import { useLayoutEffect, useRef, useState } from 'react';
import { Maximize2, X } from 'lucide-react';

/**
 * The CV in the pane, and full screen on request. One `<dialog>` holds the
 * frame in both modes: moving an iframe in the document reloads it, and every
 * load is a read in the access log, so expanding changes how the dialog is
 * shown rather than where the frame lives.
 */
export function CvViewer(props: { id: string; name: string; onClose: () => void }): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null);
  const [expanded, setExpanded] = useState(false);

  useLayoutEffect(() => {
    const element = dialog.current;
    if (element === null) return;
    if (element.open) element.close();
    if (expanded) element.showModal();
    else element.show();
  }, [expanded]);

  return (
    <dialog
      ref={dialog}
      className="cv-viewer"
      aria-label={`${props.name}'s CV`}
      onKeyDown={(event) => {
        // Escape leaves full screen and goes no further: the pane would
        // otherwise take the same key as its own close.
        if (event.key !== 'Escape' || !expanded) return;
        event.preventDefault();
        event.stopPropagation();
        setExpanded(false);
      }}
      onCancel={(event) => {
        event.preventDefault();
        setExpanded(false);
      }}
    >
      <div className="cv-viewer-bar">
        {expanded ? (
          <button
            className="button small icon-button"
            type="button"
            aria-label="Leave full screen"
            onClick={() => setExpanded(false)}
          >
            <X aria-hidden="true" />
          </button>
        ) : (
          <>
            <button className="button small" type="button" onClick={() => setExpanded(true)}>
              <Maximize2 aria-hidden="true" />
              Expand
            </button>
            <button className="button small" type="button" onClick={props.onClose}>
              Close
            </button>
          </>
        )}
      </div>
      {/* Not sandboxed: a sandboxed frame has an opaque origin, so the Lax
          session cookie would not reach the view request. The converted DOCX
          page is kept inert by its content security policy instead. */}
      <iframe
        className="cv-viewer-frame"
        src={`/talent-api/cv/${props.id}/view`}
        title={`${props.name}'s CV`}
      />
    </dialog>
  );
}
